import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  type NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  fetchApi,
  getCsrfHandler,
  resolveApiPath,
  shouldAttachBearerAuthorization,
  shouldAttachCsrfToken,
} from '@codametrix/client-api';

export type GetBearerToken = () => Promise<string>;

export type ServiceDeskCacheProfile = 'worklist' | 'qa';

export type CreateServiceDeskApiOptions = {
  /** Default `/api/service-desk/bff` */
  bffURL?: string;
  getBearerToken: GetBearerToken;
  /** Separate Apollo caches per consumer — default worklist */
  cacheProfile?: ServiceDeskCacheProfile;
  onApolloClientReady?: (client: ApolloClient<NormalizedCacheObject>) => void;
};

export type ServiceDeskApi = {
  apollo: ApolloClient<NormalizedCacheObject>;
  /** Path-resolving fetch; Bearer attached only when session-first is off */
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  getBearerToken: GetBearerToken;
  dispose: () => void;
};

const DEFAULT_BFF_URL = '/api/service-desk/bff';

function normalizeBearerToken(token: string): string {
  if (!token) {
    return token;
  }
  return token.indexOf('Bearer') !== -1 ? token : `Bearer ${token}`;
}

function buildCache(profile: ServiceDeskCacheProfile): InMemoryCache {
  if (profile === 'qa') {
    return new InMemoryCache({
      typePolicies: {
        ManagerHomeGrader: { keyFields: false },
      },
    });
  }
  return new InMemoryCache();
}

function omitAuthorization(headers: Headers): void {
  headers.delete('Authorization');
}

function csrfProofHeaders(
  method: string | undefined,
  requestInfo: RequestInfo
): Record<string, string> {
  const csrfToken = getCsrfHandler().getToken();
  if (!csrfToken || !shouldAttachCsrfToken(method, requestInfo)) {
    return {};
  }
  return { 'X-CSRF-TOKEN': csrfToken };
}

/**
 * Shared Service Desk BFF client factory (CDMX-20574).
 * Live getBearerToken when Authorization is attached; honors
 * shouldAttachBearerAuthorization (FRONTEND_SESSION_FIRST / isSessionRenewEnabled).
 * Attaches X-CSRF-TOKEN on mutating BFF traffic (same rules as requestJSON).
 */
export function createServiceDeskApi(
  options: CreateServiceDeskApiOptions
): ServiceDeskApi {
  const getBearerToken: GetBearerToken = async () =>
    normalizeBearerToken(await options.getBearerToken());
  const bffURL = options.bffURL ?? DEFAULT_BFF_URL;
  const resolvedBffURL = resolveApiPath(bffURL);
  const cacheProfile = options.cacheProfile ?? 'worklist';

  const httpLink = createHttpLink({
    uri: resolvedBffURL,
    credentials: 'include',
  });
  const authLink = setContext(async (_, { headers }) => {
    const next: Record<string, unknown> = { ...headers };
    if (!shouldAttachBearerAuthorization()) {
      delete next.authorization;
      delete next.Authorization;
    } else {
      next.authorization = await getBearerToken();
    }
    Object.assign(next, csrfProofHeaders('POST', resolvedBffURL));
    return { headers: next };
  });

  const apollo = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: buildCache(cacheProfile),
  });
  options.onApolloClientReady?.(apollo);

  let disposed = false;

  const api: ServiceDeskApi = {
    apollo,
    getBearerToken,
    fetch: async (input, init = {}) => {
      if (disposed) {
        throw new Error('ServiceDeskApi has been disposed');
      }
      const headers = new Headers(init.headers);
      if (shouldAttachBearerAuthorization()) {
        headers.set('Authorization', await getBearerToken());
      } else {
        omitAuthorization(headers);
      }
      const method = init.method ?? 'GET';
      const csrf = csrfProofHeaders(method, input as RequestInfo);
      for (const [key, value] of Object.entries(csrf)) {
        headers.set(key, value);
      }
      return fetchApi(input as any, {
        ...init,
        credentials: init.credentials ?? 'include',
        headers,
      });
    },
    dispose: () => {
      disposed = true;
      void apollo.clearStore();
      apollo.stop();
    },
  };

  return api;
}

let activeApi: ServiceDeskApi | null = null;

/** Bind the worklist api for utils/api — must run before any REST/GraphQL helpers. */
export function setServiceDeskApi(api: ServiceDeskApi | null): void {
  activeApi = api;
}

export function getServiceDeskApi(): ServiceDeskApi {
  if (!activeApi) {
    throw new Error(
      'ServiceDeskApi is not set. Call setServiceDeskApi(createServiceDeskApi(...)) before Service Desk network calls.'
    );
  }
  return activeApi;
}

/** @internal tests */
export function resetServiceDeskApiForTests(): void {
  activeApi = null;
}
